<template>
    <div @click.stop="emitClickEvent" class="issue-item" :class="{ 'issue-item-selected': isSelected, 'inside-viewer': locationType != 'normal' }">
        <div class="priority-bar" :class="'priority-bar-' + issue.priority"></div>
        <div class="issue-grid">
            <div class="issue-body">
                <div class="title">
                    <p class="text-16 text-weight-bold text-overflow" :title="'#' + issue.code + ' - ' + issue.title">
                        <span class="issue-code text-16" :class="`${issue.priority}`">#{{ issue.code }}</span>
                        <span class="text-12 text-weight-bold">{{ issue.title }}</span>
                    </p>
                </div>
                <div class="author">
                    <p :title="$t('keyword118') + ' ' + getCreatedBy + ' ' + getCreatedAtDiff" class="text-10 text-overflow text-secondary-3">
                        {{ $t('keyword118') }} <span class="text-10 text-weight-bold text-secondary">{{ getCreatedBy }}</span> {{ getCreatedAtDiff }}
                    </p>
                    <p class="text-10 text-overflow text-secondary-3" v-if="status == 'closed'">{{ $t('keyword25') }} {{ getClosedAtDiff }}</p>
                    <p class="text-10 text-overflow text-secondary-3" v-if="status == 'in_review'">{{ $t('keyword24') }} {{ getInReviewAtDiff }}</p>
                </div>
            </div>
        </div>
        <div class="item-separator">
            <div class="bar"></div>
        </div>
    </div>
</template>

<script>
import 'vue-awesome/icons/regular/eye'

/**
 * Summary.
 * Use this component to mount a CIN issue
 *
 * @param {Object} issue - Object containing issue info
 * @param {Boolean} isSelected - Defines if issue is selected or not.
 * @param {String} locationType - viewer/normal
 * @param {String} status - The selected tab status
 *
 * @event click - On issue item click.
 *
 */
export default {
    props: {
        issue: {
            type: Object,
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
        locationType: String,
        status: String,
    },
    computed: {
        getCreatedBy() {
            return this.issue.created_by.name + ' ' + this.issue.created_by.surname
        },
        getCreatedAtDiff() {
            let now = this.$moment()
            let createdAt = this.$moment(this.issue.created_at, 'DD/MM/YYYY HH:mm:ss')

            let hours = now.diff(createdAt, 'hours')
            let days = now.diff(createdAt, 'days')
            if (hours < 24) {
                if (this.$t('keyword119') == 'ago') {
                    return hours + ' ' + this.$t('keyword120') + ' ' + this.$t('keyword119')
                } else {
                    return this.$t('keyword119') + ' ' + hours + ' ' + this.$t('keyword120')
                }
            } else {
                if (this.$t('keyword119') == 'ago') {
                    return days + ' ' + this.$t('keyword121') + ' ' + this.$t('keyword119')
                } else {
                    return this.$t('keyword119') + ' ' + days + ' ' + this.$t('keyword121')
                }
            }
        },
        getClosedAtDiff() {
            if (this.issue.closed_at) {
                let now = this.$moment()
                let closedAt = this.$moment(this.issue.closed_at, 'DD/MM/YYYY HH:mm:ss')
                let hours = now.diff(closedAt, 'hours')
                let days = now.diff(closedAt, 'days')
                if (hours < 24) {
                    if (this.$t('keyword119') == 'ago') {
                        return hours + ' ' + this.$t('keyword120') + ' ' + this.$t('keyword119')
                    } else {
                        return this.$t('keyword119') + ' ' + hours + ' ' + this.$t('keyword120')
                    }
                } else {
                    if (this.$t('keyword119') == 'ago') {
                        return days + ' ' + this.$t('keyword121') + ' ' + this.$t('keyword119')
                    } else {
                        return this.$t('keyword119') + ' ' + days + ' ' + this.$t('keyword121')
                    }
                }
            }
            return ''
        },
        getInReviewAtDiff() {
            if (this.issue.to_review_at) {
                let now = this.$moment()
                let inReviewAt = this.$moment(this.issue.to_review_at, 'DD/MM/YYYY HH:mm:ss')
                let hours = now.diff(inReviewAt, 'hours')
                let days = now.diff(inReviewAt, 'days')
                if (hours < 24) {
                    if (this.$t('keyword119') == 'ago') {
                        return hours + ' ' + this.$t('keyword120') + ' ' + this.$t('keyword119')
                    } else {
                        return this.$t('keyword119') + ' ' + hours + ' ' + this.$t('keyword120')
                    }
                } else {
                    if (this.$t('keyword119') == 'ago') {
                        return days + ' ' + this.$t('keyword121') + ' ' + this.$t('keyword119')
                    } else {
                        return this.$t('keyword119') + ' ' + days + ' ' + this.$t('keyword121')
                    }
                }
            }
            return ''
        },
        getLocation() {
            let keyword = this.$t('keyword177')
            let location = ''

            if (this.issue.floorplan.map) {
                location = this.$t('keyword49') + '/' + this.issue.viewpoint.name
            } else if (this.issue.sector) {
                location = this.issue.sector.name + ' / ' + this.issue.floorplan.name + ' / ' + this.issue.viewpoint.name
            } else {
                location = this.issue.floorplan.name + ' / ' + this.issue.viewpoint.name
            }

            return keyword + ' ' + location
        },
    },
    methods: {
        /**
         * Summary.
         * Use this method to emit an issue click.
         *
         * @param {Object} event - The click event object.
         */
        emitClickEvent(event) {
            if (this.locationType == 'search') {
                this.goToLocation()
            } else {
                this.$emit('click', event)
            }
        },

        /**
         * Summary.
         * Go to issue location.
         */
        goToLocation(event) {
            this.$emit('click', event)
        },
    },
}
</script>

<style lang="scss" scoped>
.issue-item {
    height: 72px;
    width: 100%;
    background: var(--primary-3);
    position: relative;
    padding: 8px 16px;
    cursor: pointer;
    &.inside-viewer {
        height: 62px;

        .title span {
            font-size: 12px;
        }

        .title {
            p {
                line-height: 14px;
            }
        }

        .issue-comment {
            opacity: 0;
            transition: all 0.3s;
        }
    }

    &:hover {
        background-color: var(--primary-4);
        .issue-comment {
            opacity: 1;
            transition: all 0.3s;
        }
    }

    .priority-bar {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translate(0, -50%);
        height: 66%;
        width: 2px;
        transition: all 0.3s;
        border-radius: 0 4px 4px 0;
        &.priority-bar-low {
            background: var(--accent);
        }
        &.priority-bar-medium {
            background: var(--semantic-yellow-dark);
        }
        &.priority-bar-high {
            background: var(--semantic-red-dark);
        }
    }
    .issue-grid {
        width: 100%;
        display: grid;
        grid-template-columns: minmax(1px, 1fr) min-content;
        column-gap: 16px;
    }
    .title {
        .text-16.text-weight-bold {
            .issue-code {
                margin-right: 4px;
                &.high {
                    color: var(--semantic-red);
                }
                &.medium {
                    color: var(--semantic-yellow);
                }
                &.low {
                    color: var(--accent);
                }
            }
        }
    }
    &.issue-item-selected {
        background: var(--primary-2);
        .priority-bar {
            width: 4px;
            height: 100%;
            transition: all 0.3s;
            &.priority-bar-low {
                background: var(--secondary);
            }
            &.priority-bar-medium {
                background: var(--semantic-yellow);
            }
            &.priority-bar-high {
                background: var(--semantic-red);
            }
        }
    }
}
</style>
